import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  Chip
} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import MatchEventRow from './events/MatchEventRow';
import MultiDatasetMatchEventRow from './events/MultiDatasetMatchEventRow';
import AssignmentEventRow from './events/AssignmentEventRow';
import GroupMatchEventRow from './events/GroupMatchEventRow';
import NoteEventRow from './events/NoteEventRow';
import PromotionEventRow from './events/PromotionEventRow';
import DetectionEventRow from './events/DetectionEventRow';
import SOIEventRow from './events/SOIEventRow';
import UnknownPromotionEventRow from './events/UnknownPromotionEventRow';
import { useEventRelationships } from './hooks/useEventRelationships';

/**
 * EventTableV2 - Refactored event table using component-per-type architecture
 *
 * Key improvements over EventTable:
 * - Event-specific components (MatchEventRow, AssignmentEventRow)
 * - React.memo optimizations to prevent unnecessary re-renders
 * - Incremental rendering (only new events render)
 * - Cleaner separation of concerns
 * - Better maintainability
 *
 * Performance characteristics:
 * - When new event arrives: Only 1 new row renders
 * - When assignment expands: Only that assignment row re-renders
 * - Existing events: Protected by React.memo (no re-render)
 */
export default function EventTableV2({
  events,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  onLocationClick,
  onAssignmentClick,
  onCreateStatement
}) {
  // Build parent-child relationship maps
  const { assignments, groups, topLevelEvents } = useEventRelationships(events);

  // Debug: Log promotion events
  React.useEffect(() => {
    const promotionEvents = events.filter(e => e.eventType === 'promotion');
    if (promotionEvents.length > 0) {
      console.log('[EventTableV2] Promotion events in events array:', promotionEvents.length);
      console.log('[EventTableV2] Promotion events in topLevelEvents:', topLevelEvents.filter(e => e.eventType === 'promotion').length);
    }
  }, [events, topLevelEvents]);

  // Track which assignments/groups are expanded
  const [expandedAssignments, setExpandedAssignments] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Auto-expand new group matches
  useEffect(() => {
    if (groups.size > 0) {
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        // Add all group IDs to expanded set
        groups.forEach((childEvents, groupId) => {
          newSet.add(groupId);
        });
        return newSet;
      });
    }
  }, [groups]);

  // Auto-expand new assignments
  useEffect(() => {
    if (assignments.size > 0) {
      setExpandedAssignments(prev => {
        const newSet = new Set(prev);
        // Add all assignment IDs to expanded set
        assignments.forEach((childEvents, assignmentId) => {
          newSet.add(assignmentId);
        });
        return newSet;
      });
    }
  }, [assignments]);

  // Toggle assignment expand/collapse
  const handleToggleAssignment = (assignmentId) => {
    setExpandedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  // Toggle group expand/collapse
  const handleToggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Get address for an event - uses backend geocoded address or coordinates
  const getAddress = (event) => {
    if (!event.location) return undefined;

    // Use address from backend (geocoded by Lambda)
    if (event.metadata?.address?.short) {
      return event.metadata.address.short;
    }

    // Fallback to coordinates for old events without backend address
    return `${event.location.latitude.toFixed(4)}, ${event.location.longitude.toFixed(4)}`;
  };

  return (
    <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Scrollable body with InfiniteScroll */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }} id="aggregatorScrollableDiv">
        <InfiniteScroll
          dataLength={topLevelEvents.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          }
          endMessage={
            topLevelEvents.length > 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 1 }}>
                No more events
              </Typography>
            )
          }
          scrollableTarget="aggregatorScrollableDiv"
        >
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
              <TableBody>
                {topLevelEvents.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '16px' }}>
                      <Typography variant="body2" color="text.secondary">
                        No events found
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  topLevelEvents.map((event) => {
                    // Render event based on type
                    if (event.eventType === 'match' || event.eventType === 'soi_match') {
                      return (
                        <MatchEventRow
                          key={event.eventId}
                          event={event}
                          isChild={false}
                          address={getAddress(event)}
                          onLocationClick={onLocationClick}
                          onCreateStatement={onCreateStatement}
                        />
                      );
                    }

                    if (event.eventType === 'multi_dataset_match') {
                      return (
                        <MultiDatasetMatchEventRow
                          key={event.eventId}
                          event={event}
                          isChild={false}
                          address={getAddress(event)}
                          onLocationClick={onLocationClick}
                        />
                      );
                    }

                    if (event.eventType === 'assignment') {
                      const childEvents = assignments.get(event.eventId) || [];
                      return (
                        <AssignmentEventRow
                          key={event.eventId}
                          event={event}
                          childEvents={childEvents}
                          expanded={expandedAssignments.has(event.eventId)}
                          onToggleExpand={() => handleToggleAssignment(event.eventId)}
                          getAddress={getAddress}
                          onLocationClick={onLocationClick}
                          onAssignmentClick={onAssignmentClick}
                        />
                      );
                    }

                    if (event.eventType === 'group_match') {
                      const childEvents = groups.get(event.eventId) || [];
                      return (
                        <GroupMatchEventRow
                          key={event.eventId}
                          event={event}
                          childEvents={childEvents}
                          expanded={expandedGroups.has(event.eventId)}
                          onToggleExpand={() => handleToggleGroup(event.eventId)}
                          isChild={false}
                          address={getAddress(event)}
                          getAddress={getAddress}
                          onLocationClick={onLocationClick}
                        />
                      );
                    }

                    if (event.eventType === 'note') {
                      return (
                        <NoteEventRow
                          key={event.eventId}
                          event={event}
                          isChild={false}
                        />
                      );
                    }

                    if (event.eventType === 'promotion') {
                      return (
                        <PromotionEventRow
                          key={event.eventId}
                          event={event}
                          isChild={false}
                          address={getAddress(event)}
                          onLocationClick={onLocationClick}
                        />
                      );
                    }

                    if (event.eventType === 'detection') {
                      return (
                        <DetectionEventRow
                          key={event.eventId}
                          event={event}
                          isChild={false}
                          address={getAddress(event)}
                          onLocationClick={onLocationClick}
                        />
                      );
                    }

                    if (event.eventType === 'soi_event') {
                      return (
                        <SOIEventRow
                          key={event.eventId}
                          event={event}
                          isChild={false}
                          address={getAddress(event)}
                          onLocationClick={onLocationClick}
                        />
                      );
                    }

                    if (event.eventType === 'unknown_promotion') {
                      console.log('[EventTableV2] Matched unknown_promotion event:', event.eventId);
                      return (
                        <UnknownPromotionEventRow
                          key={event.eventId}
                          event={event}
                          isChild={false}
                          address={getAddress(event)}
                          onLocationClick={onLocationClick}
                        />
                      );
                    }

                    // Fallback for unknown event types
                    return (
                      <TableRow key={event.eventId} sx={{ backgroundColor: '#ffebee' }}>
                        <TableCell colSpan={5} style={{ padding: '8px' }}>
                          <Typography variant="caption" color="text.secondary">
                            Unknown event type: {event.eventType}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      </Box>
    </Box>
  );
}
